import React, { PropsWithChildren } from 'react'
import clsx from 'clsx'
import { Table as TableStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import { CommonProps } from '../helperTypes'

type TableSection = {
  section: 'head' | 'body' | 'footer'
}
const TableSectionContext = React.createContext<TableSection | null>(null)

export type TableProps = PropsWithChildren<CommonProps>

const useStyles = createUseStyles(TableStyles, 'table')

export const Table = ({ className, children, ...rest }: TableProps) => {
  const classes = useStyles()
  return (
    <div className={classes.container}>
      <table className={clsx(className, classes.root)} {...rest}>
        {children}
      </table>
    </div>
  )
}

export default Table

export type TableBodyProps = PropsWithChildren<CommonProps>
export const TableBody = ({ className, children, ...rest }: TableBodyProps) => {
  const classes = useStyles()
  return (
    <tbody className={clsx(className, classes.body)} {...rest}>
      {children}
    </tbody>
  )
}

export type TableHeadProps = PropsWithChildren<CommonProps>
export const TableHead = ({ className, children, ...rest }: TableHeadProps) => {
  const classes = useStyles()
  return (
    <TableSectionContext.Provider value={{ section: 'head' }}>
      <thead className={clsx(className, classes.head)} {...rest}>
        {children}
      </thead>
    </TableSectionContext.Provider>
  )
}

export type TableRowProps = PropsWithChildren<CommonProps>

export const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, children, ...rest }: TableRowProps, ref) => {
    const classes = useStyles()
    return (
      <tr className={clsx(className, classes.row)} {...rest} ref={ref}>
        {children}
      </tr>
    )
  },
)

export type TableCellProps = {
  /** Sets the text-align on the table cell content. */
  align?: 'left' | 'right'
  head?: boolean
} & CommonProps

export const TableCell = ({ align, className, children, head = false, ...rest }: TableCellProps) => {
  const classes = useStyles()
  const ctx = React.useContext(TableSectionContext)

  const Component = ctx && ctx.section === 'head' ? 'th' : 'td'
  return (
    <Component
      className={clsx(className, classes.cell, align === 'right' && classes.cell_right, head && classes.cell_head)}
      {...rest}
    >
      {children}
    </Component>
  )
}

export type TableEmptyProps = PropsWithChildren<CommonProps> & {
  colspan: number
  label?: string
}
export const TableEmpty = ({ className, children, label = 'No data to display', ...rest }: TableEmptyProps) => {
  const classes = useStyles()
  return (
    <tr>
      <td className={className} {...rest}>
        <div className={classes.empty}>
          <p>{label}</p>
          <>{children}</>
        </div>
      </td>
    </tr>
  )
}
